import { lazy, Suspense, useEffect, useState } from 'react';
import GamesSelecterDialog from './dialogs/gamesSelecterDialog';
import CharacterBioDialog from './dialogs/characterBioDialog';
import MusicPlayerDialog from './dialogs/musicPlayerDialog';
import Project1Dialog from './dialogs/Projects/priceCalculatorDialog';
import Project2Dialog from './dialogs/Projects/seasonAppDialog';
import Project3Dialog from './dialogs/Projects/dataPredictionDialog';
import Project4Dialog from './dialogs/Projects/websiteRemakeDialog';
import Project5Dialog from './dialogs/Projects/WorkTimeTrackerDialog';
import Project6Dialog from './dialogs/Projects/ZenRadarDialog';
import Project7Dialog from './dialogs/Projects/KitchenSyncDialog';
import Project8Dialog from './dialogs/Projects/CoupleQuestsDialog';
import Project9Dialog from './dialogs/Projects/BillKeeperDialog';
import { LeaderboardGameId } from '../services/leaderboardService';

import { grid } from 'ldrs'
import { useTranslation } from 'react-i18next';
const Spline = lazy(() => import('@splinetool/react-spline'));

grid.register()

const DESKTOP_SPLINE_SCENE = 'https://prod.spline.design/4tsgLpJzbEwMAvqs/scene.splinecode';
const MOBILE_SPLINE_SCENE = 'https://prod.spline.design/WtpdnqUHOhs-ZG1s/scene.splinecode';

// Dialog identifiers
const dialogs = {
  NONE: null,
  ARCADE_MACHINE: 'ArcadeMachine',
  CALCULATOR: 'Calculator',
  CARDS: 'Cards',
  DATABASE: 'Database',
  WEBSITE: 'Website',
  CHARACTER: 'CharacterBio',
  MUSIC_PLAYER: 'MusicPlayer',
  WORKTIMETRACKER: 'WorkTimeTracker',
  ZENRADAR: 'ZenRadar',
  KITCHENSYNC: 'KitchenSync',
  COUPLEQUESTS: 'CoupleQuests',
  BILLKEEPER: 'BillKeeper'
};

interface RoomAnimationProps {
  onSwitchView?: () => void;
}

function RoomAnimation({ onSwitchView }: RoomAnimationProps) {
  const { t } = useTranslation();
  const [openDialogType, setOpenDialogType] = useState(dialogs.NONE);
  const [launchGameId, setLaunchGameId] = useState<LeaderboardGameId | undefined>(undefined);

  const [isLoading, setIsLoading] = useState(true);
  const [isPhoneScreen, setIsPhoneScreen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');

    const updatePhoneScreen = () => {
      setIsPhoneScreen(mediaQuery.matches);
    };

    updatePhoneScreen();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updatePhoneScreen);
    } else {
      mediaQuery.addListener(updatePhoneScreen);
    }

    window.addEventListener('orientationchange', updatePhoneScreen);
    window.addEventListener('resize', updatePhoneScreen);

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', updatePhoneScreen);
      } else {
        mediaQuery.removeListener(updatePhoneScreen);
      }
      window.removeEventListener('orientationchange', updatePhoneScreen);
      window.removeEventListener('resize', updatePhoneScreen);
    };
  }, []);

  const openArcadeDialog = (gameId?: LeaderboardGameId) => {
    setLaunchGameId(gameId);
    setOpenDialogType(dialogs.ARCADE_MACHINE as any);
  };

  function handleSceneInteraction(e: { target?: { name?: unknown; }; }) {
    const targetName = String(e?.target?.name ?? '').trim();
    const normalizedName = targetName.toLowerCase();
    const compactName = normalizedName.replace(/[^a-z0-9]/g, '');

    // Spline object names are easy to drift (spaces, underscores, casing),
    // so match on robust aliases/keywords instead of one exact string.
    if (compactName.includes('tetrisposter') || compactName.includes('tetris')) {
      openArcadeDialog('tetris');
      return;
    }

    if (compactName.includes('brickbreakerposter') || compactName.includes('brickbreaker')) {
      openArcadeDialog('brickbreaker');
      return;
    }

    if (compactName === 'arcademachine' || compactName.includes('arcade')) {
      openArcadeDialog(undefined);
      return;
    }

    if (compactName === 'calculator' || compactName.includes('pricecalculator')) {
      setOpenDialogType(dialogs.CALCULATOR as any);
      return;
    }

    if (compactName === 'cards' || compactName.includes('seasonapp')) {
      setOpenDialogType(dialogs.CARDS as any);
      return;
    }

    if (compactName === 'database' || compactName.includes('dataprediction')) {
      setOpenDialogType(dialogs.DATABASE as any);
      return;
    }

    if (compactName === 'website' || compactName.includes('websiteremake')) {
      setOpenDialogType(dialogs.WEBSITE as any);
      return;
    }

    if (compactName.includes('worktimetracker')) {
      setOpenDialogType(dialogs.WORKTIMETRACKER as any);
      return;
    }

    if (compactName.includes('zenradar')) {
      setOpenDialogType(dialogs.ZENRADAR as any);
      return;
    }

    if (
      compactName.includes('kitchensync')
      || compactName.includes('kitchsync')
      || compactName.includes('kitchen')
    ) {
      setOpenDialogType(dialogs.KITCHENSYNC as any);
      return;
    }

    if (
      compactName.includes('couplequests')
      || compactName.includes('couplequest')
      || compactName.includes('couple')
    ) {
      setOpenDialogType(dialogs.COUPLEQUESTS as any);
      return;
    }

    if (
      compactName.includes('billkeeper')
      || compactName.includes('billkeep')
      || compactName.includes('bill')
    ) {
      setOpenDialogType(dialogs.BILLKEEPER as any);
      return;
    }

    if (
      compactName.includes('earphone')
      || compactName.includes('earphones')
      || compactName.includes('headset')
      || compactName.includes('headphone')
      || compactName.includes('headphones')
      || compactName.includes('music')
    ) {
      setOpenDialogType(dialogs.MUSIC_PLAYER as any);
      return;
    }

    if (
      normalizedName.includes('character')
      || normalizedName.includes('avatar')
      || normalizedName.includes('player')
    ) {
      setOpenDialogType(dialogs.CHARACTER as any);
      return;
    }

    if (targetName && process.env.NODE_ENV !== 'production') {
      console.debug('[Spline] Unhandled object click:', targetName);
    }
  }

  const handleCloseDialog = () => {
    setOpenDialogType(dialogs.NONE);
    setLaunchGameId(undefined);
  };
  
  return (
    <div style={{
      display: 'flex',
      flex: '1',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      backgroundColor: isLoading ? '#6F6F78' : 'white',
      width: '100vw',
      height: '100dvh',
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
      touchAction: 'manipulation'}}>
      <Suspense fallback={<l-grid size="500" speed="5" color="yellow" ></l-grid>}>
        <Spline
          key={isPhoneScreen ? 'mobile' : 'desktop'}
          scene={isPhoneScreen ? MOBILE_SPLINE_SCENE : DESKTOP_SPLINE_SCENE}
          onMouseDown={handleSceneInteraction}
          onLoad={() => setIsLoading(false)}
        />
        {isLoading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
            zIndex: 10
          }}>
            <l-grid size={isPhoneScreen ? '220' : '500'} speed="5" color="yellow" ></l-grid>
            <p style={{
              fontFamily: '"Press Start 2P", cursive',
              fontSize: isPhoneScreen ? '10px' : '14px',
              color: '#333',
              textAlign: 'center',
              maxWidth: '90%',
              margin: 0,
              lineHeight: '1.5'
            }}>
              Loading interactive portfolio experience...<br />
              Have fun clicking around! 🎮
            </p>
          </div>
        )}
        {openDialogType === dialogs.CHARACTER && <CharacterBioDialog open onClose={handleCloseDialog} />}
        {openDialogType === dialogs.MUSIC_PLAYER && <MusicPlayerDialog open onClose={handleCloseDialog} />}
        {openDialogType === dialogs.ARCADE_MACHINE && <GamesSelecterDialog open onClose={handleCloseDialog} launchGameId={launchGameId} />}
        {openDialogType === dialogs.CALCULATOR && <Project1Dialog open onClose={handleCloseDialog} />}
        {openDialogType === dialogs.CARDS && <Project2Dialog open onClose={handleCloseDialog} />}
        {openDialogType === dialogs.DATABASE && <Project3Dialog open onClose={handleCloseDialog} />}
        {openDialogType === dialogs.WEBSITE && <Project4Dialog open onClose={handleCloseDialog} />}
        {openDialogType === dialogs.WORKTIMETRACKER && <Project5Dialog open onClose={handleCloseDialog} />}
        {openDialogType === dialogs.ZENRADAR && <Project6Dialog open onClose={handleCloseDialog} />}
        {openDialogType === dialogs.KITCHENSYNC && <Project7Dialog open onClose={handleCloseDialog} />}
        {openDialogType === dialogs.COUPLEQUESTS && <Project8Dialog open onClose={handleCloseDialog} />}
        {openDialogType === dialogs.BILLKEEPER && <Project9Dialog open onClose={handleCloseDialog} />}
      </Suspense>

      {onSwitchView && (
        <button
          type="button"
          onClick={onSwitchView}
          style={{
            position: 'absolute',
            top: isPhoneScreen ? 'auto' : 16,
            bottom: isPhoneScreen ? 16 : 'auto',
            right: 16,
            zIndex: 30,
            fontFamily: '"Press Start 2P", cursive',
            fontSize: 10,
            color: '#FFD700',
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            border: '2px solid #FFD700',
            padding: '8px 10px',
            cursor: 'pointer',
            lineHeight: 1.4,
            textAlign: 'center',
          }}
        >
          🕹️ {t('portfolioChooser.switchView')}
        </button>
      )}
  </div>
  );
}

export default RoomAnimation;